import React, {useMemo, useState} from "react";
import cn from "classnames";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Button, Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle} from "biggerui";
import {API} from "api";
import {CHANNELS} from "data/products";
import {getTemplateDefinitionByKey} from "data/templateManifest";
import {liveTemplateSelection, type TemplatePool} from "data/templateSlots";
import {TEMPLATE_SLOTS_QUERY_KEY, type TemplateSlotsDoc} from "hooks/useTemplateSelection";
import {buildFleetCards, filterFleetCards, poolCounts, POOLS, type FleetMember} from "./fleet";
import {TemplateCard} from "./TemplateCard";
import "./index.css";

const POOL_HINTS: Record<TemplatePool, string> = {
  "on-air": "The designs the pages draw now.",
  candidate:
    "Designs newer than the one on air in their place. A new design comes from code: a new template key added to the place's members (docs/plan-template-fleet.md, §4).",
  archive:
    "Designs older than the one on air in their place. Put one on air to bring it back; saved links that use it never stopped drawing it.",
};

const EMPTY_POOL_TEXT: Record<TemplatePool, string> = {
  "on-air": "No design is on air for this filter.",
  candidate: "No candidate designs yet.",
  archive: "No archived designs yet. A design that a newer one replaces moves here.",
};

function labelOf(key: string): string {
  try {
    const definition = getTemplateDefinitionByKey(key);
    return `${definition.label} (${definition.id})`;
  } catch {
    return key;
  }
}

/**
 * The parking lot of every template (admin): what the pages draw now,
 * the candidates, and the archive, each with a live preview.
 */
const TemplatesPage = () => {
  const queryClient = useQueryClient();
  const [pool, setPool] = useState<TemplatePool>("on-air");
  const [channel, setChannel] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<FleetMember | null>(null);

  const query = useQuery<TemplateSlotsDoc>({
    queryKey: TEMPLATE_SLOTS_QUERY_KEY,
    queryFn: () => API.fetchTemplateSlots(),
    refetchOnWindowFocus: true,
  });
  const live = useMemo(
    () => (query.data ? liveTemplateSelection(query.data.chosen) : null),
    [query.data]
  );
  const cards = useMemo(() => (live ? buildFleetCards(live.selection) : []), [live]);
  const counts = useMemo(() => poolCounts(cards), [cards]);
  const visibleCards = useMemo(
    () => filterFleetCards(cards, {pool, channel, search}),
    [cards, pool, channel, search]
  );
  const channels = useMemo(
    () => CHANNELS.filter((entry: {value: string}) => cards.some((card) => card.channels.includes(entry.value))),
    [cards]
  );

  const putOnAir = useMutation({
    mutationFn: (member: FleetMember) =>
      API.chooseTemplateMember({rev: query.data?.rev ?? 0, member: member.key}),
    onSuccess: (doc: TemplateSlotsDoc) => {
      queryClient.setQueryData(TEMPLATE_SLOTS_QUERY_KEY, doc);
      setPending(null);
    },
    onError: () => {
      // A stale revision or a lost write: show the choice as it is now.
      queryClient.invalidateQueries({queryKey: TEMPLATE_SLOTS_QUERY_KEY});
    },
  });

  // The places the pending member takes, and the designs it replaces there.
  const impact = useMemo(() => {
    if (!pending) return [];
    const card = cards.find((entry) => entry.key === pending.key);
    if (!card) throw new Error(`No card for template "${pending.key}"`);
    return card.placements
      .filter((placement) => placement.members.length > 1)
      .map((placement) => ({
        page: placement.page,
        outgoing: placement.members.find((member) => member.isOnAir && member.key !== pending.key),
      }));
  }, [pending, cards]);

  if (query.isPending) return <div className="admin-templates">Loading…</div>;
  if (query.isError || !live) {
    return (
      <div className="admin-templates">
        <div className="admin-templates-error" role="alert">
          The templates on air could not load: {(query.error as Error | null)?.message}
          <Button size="sm" onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-templates">
      <header className="admin-templates-header">
        <h1 className="admin-templates-title">Templates</h1>
        <p className="admin-templates-notice">
          A change goes live at once on staging and on production (one database). A page shows it
          on its next load. Saved links keep the designs they were saved with.
        </p>
        {live.unknown.length > 0 && (
          <p className="admin-templates-error" role="alert">
            The choice names designs this version does not have: {live.unknown.join(", ")}. Their
            places draw the code default.
          </p>
        )}
      </header>

      <div className="admin-templates-pools" role="tablist" aria-label="Pools">
        {POOLS.map((entry) => (
          <button
            key={entry.value}
            role="tab"
            aria-selected={pool === entry.value}
            className={cn("admin-templates-pool", {"is-active": pool === entry.value})}
            onClick={() => setPool(entry.value)}
          >
            {entry.label} <span className="admin-templates-count">{counts[entry.value]}</span>
          </button>
        ))}
      </div>
      <p className="admin-templates-pool-hint">{POOL_HINTS[pool]}</p>

      <div className="admin-templates-filters">
        <div className="admin-templates-channels" role="group" aria-label="Channel">
          <button
            className={cn("admin-templates-chip", {"is-active": channel === null})}
            onClick={() => setChannel(null)}
          >
            All channels
          </button>
          {channels.map((entry: {value: string; label: string}) => (
            <button
              key={entry.value}
              className={cn("admin-templates-chip", {"is-active": channel === entry.value})}
              onClick={() => setChannel(entry.value)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="admin-templates-search"
          placeholder="Search by name, id or size"
          aria-label="Search templates"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {visibleCards.length === 0 ? (
        <p className="admin-templates-empty">{EMPTY_POOL_TEXT[pool]}</p>
      ) : (
        <div className="admin-templates-grid">
          {visibleCards.map((card) => (
            <TemplateCard
              key={card.key}
              card={card}
              isBusy={putOnAir.isPending}
              onPutOnAir={(member) => {
                putOnAir.reset();
                setPending(member);
              }}
            />
          ))}
        </div>
      )}

      <section className="admin-templates-history">
        <h2>History</h2>
        {query.data.log.length === 0 ? (
          <p className="admin-templates-empty">Nobody changed the designs on air yet.</p>
        ) : (
          <ol>
            {[...query.data.log].reverse().map((entry) => (
              <li key={`${entry.at}-${entry.member}`}>
                {new Date(entry.at).toLocaleString()} · {entry.by} put {labelOf(entry.member)} on
                air
              </li>
            ))}
          </ol>
        )}
      </section>

      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogHeader>
          <DialogTitle>Put {pending ? labelOf(pending.key) : ""} on air?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <ul className="admin-templates-impact">
            {impact.map(({page, outgoing}) => (
              <li key={page}>
                {page}: {outgoing ? `replaces ${outgoing.label} (${outgoing.id})` : "already on air"}
              </li>
            ))}
          </ul>
          <p>
            It goes live at once on staging and on production. The replaced design moves to the
            archive; saved links keep drawing it.
          </p>
          {putOnAir.isError && (
            <p className="admin-templates-error" role="alert">
              {(putOnAir.error as Error).message}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button color="secondary" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button
            loading={putOnAir.isPending}
            disabled={!pending || putOnAir.isPending}
            onClick={() => pending && putOnAir.mutate(pending)}
          >
            Put on air
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
